// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, CustomerValidationDto } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import styled, { css } from "styled-components";

type BaseAddressModelWithValidation = BaseAddressModel & { validation?: CustomerValidationDto | null };

interface OwnProps {
    customer: BaseAddressModelWithValidation;
    isSelected?: boolean;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    extendedStyles?: CustomerCardStyles;
    isOverlay?: boolean;
}

export interface CustomerCardStyles {
    container?: GridContainerProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
    editGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    editLink?: LinkPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    addressWrapper?: InjectableCss;
    editWrapper?: InjectableCss;
    actionsContainer?: InjectableCss;
}

export const customerCardStyles: CustomerCardStyles = {
    container: {
        css: css`
            padding: 1rem 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    addressGridItem: { width: 6 },
    actionsGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
        `,
    },
    selectButton: { variant: "tertiary" },
    editLink: {
        css: css`
            margin-top: 1rem;
        `,
    },
};

const ActionsWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const CustomerCard = ({ customer, isSelected, onEdit, extendedStyles, onSelect, isOverlay }: OwnProps) => {
    const { allowShipToAddressEdit } = useAppSelector(state => getSettingsCollection(state).customerSettings);
    const [styles] = useState(() => mergeToNew(customerCardStyles, extendedStyles));

    const handleEditClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => onEdit?.(event, customer);

    const hasEditableFields = customer.validation && Object.values(customer.validation).some(o => !o.isDisabled);
    return (
        // XNG-Changes: changing gridcontainer to styledwrapper
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.addressWrapper}>
                <AddressInfoDisplay
                    {...customer}
                    state={customer.state?.abbreviation}
                    country={customer.country?.abbreviation}
                    extendedStyles={styles.address}
                    // XNG-Changes
                    attention=""
                />
            </StyledWrapper>
            {/* XNG-Changes */}
            {isOverlay && hasEditableFields && onEdit && allowShipToAddressEdit && (
                <StyledWrapper {...styles.editWrapper}>
                    <Link
                        {...styles.editLink}
                        onClick={handleEditClick}
                        data-test-selector={`customerCard_edit_${customer.id}`}
                    >
                        {translate("Edit")}
                    </Link>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.actionsContainer}>
                <ActionsWrapper {...styles.actionsWrapper}>
                    <Button
                        {...styles.selectButton}
                        onClick={() => onSelect(customer)}
                        disabled={isSelected}
                        data-test-selector={`customerCard_select_${customer.id}`}
                    >
                        {isSelected ? "Selected" : "Select"}
                    </Button>
                    {/* XNG-Changes */}
                    {!isOverlay && hasEditableFields && onEdit && allowShipToAddressEdit && (
                        <Link
                            {...styles.editLink}
                            onClick={handleEditClick}
                            data-test-selector={`customerCard_edit_${customer.id}`}
                        >
                            {translate("Edit")}
                        </Link>
                    )}
                </ActionsWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default CustomerCard;
